import { useState, type MouseEvent } from 'react';
import { Box } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LayersRowMenu } from '@/common/presentation/components/layers';
import type { LayersRowMenuItem } from '@/common/presentation/components/layers';
import RwIconButton from '@/common/presentation/components/rw/RwIconButton';
import { useCanvasStructure, type CanvasColumnActions } from './CanvasStructureContext';

export interface CanvasColumnMenuProps {
  sectionId: string;
  /** Zero-based column index within the section. */
  column: number;
  /** 1-based section position, for the accessible name ("Column 2 of Section 1"). */
  sectionPosition: number;
  testId: string;
}

function menuItems(actions: CanvasColumnActions, { sectionId, column }: CanvasColumnMenuProps): LayersRowMenuItem[] {
  const canDuplicate = actions.canDuplicate(sectionId);
  const canRemove = actions.canRemove(sectionId);
  return [
    {
      label: 'Duplicate column',
      icon: <ContentCopyOutlinedIcon fontSize="small" />,
      onClick: () => actions.duplicate(sectionId, column),
      disabled: !canDuplicate,
      hint: canDuplicate ? undefined : 'Up to 3 columns'
    },
    {
      label: 'Delete column',
      icon: <DeleteOutlineIcon fontSize="small" />,
      onClick: () => actions.remove(sectionId, column),
      destructive: true,
      disabled: !canRemove,
      hint: canRemove ? undefined : 'Delete the section instead'
    }
  ];
}

/**
 * The ⋮ menu of one column on the block editor canvas (LLAMS-699): Duplicate column (off at the 3-column
 * maximum) and Delete column (off for a section's only column), the same two actions as the Sections
 * panel. Drawn just BELOW the column's bottom-right corner, revealed with the section's hover / focus
 * chrome (and kept while its menu is open); renders nothing wherever the host offers no column actions.
 * It must not sit over the column: the last element's "Insert a dynamic value" button is at that same
 * corner, and a ⋮ drawn on top of it made the button unreachable (e2e field-list-editor, variable-insertion).
 */
export function CanvasColumnMenu(props: CanvasColumnMenuProps) {
  const actions = useCanvasStructure()?.columnMenu;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!actions) return null;

  const { column, sectionPosition, testId } = props;
  const name = `Column ${column + 1} of Section ${sectionPosition}`;
  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  };
  const items = menuItems(actions, props).map((item, index) => ({ ...item, testId: `${testId}:${index === 0 ? 'duplicate' : 'delete'}` }));

  return (
    <Box
      data-column-chrome
      data-menu-open={anchor ? 'true' : undefined}
      sx={{ position: 'absolute', right: 'var(--size-4, 4px)', top: '100%', zIndex: 3 }}
    >
      <RwIconButton
        tier="primary"
        size="sm"
        footprintWidth="var(--size-24)"
        onClick={openMenu}
        aria-label={`More actions — ${name}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        data-testid={`${testId}:menu`}
      >
        <MoreVertIcon />
      </RwIconButton>
      <LayersRowMenu anchorEl={anchor} onClose={() => setAnchor(null)} items={items} testId={`${testId}:menu-list`} />
    </Box>
  );
}
